import { FC } from "react";

import { Badge } from "../../../../molecules/Badge/Badge";

const MAX_STATE_BADGE_LENGTH = 40;

const RowBadge: FC<{
  message: string;
  type: "error" | "warning" | "neutral";
}> = ({ message, type }) => (
  <Badge
    type={type}
    isStateBadge={true}
    title={message}
    className={`break-all mx-auto${message.length > MAX_STATE_BADGE_LENGTH ? " rounded-xl" : ""}`}
  />
);

// Neutral hint, visible only while focus is inside the row (FieldsGroup's group/row).
// Wrapper: Badge's own `flex` would compete with `hidden`.
const FocusHintBadge: FC<{ message: string }> = ({ message }) => (
  <div className="hidden group-focus-within/row:block">
    <RowBadge message={message} type="neutral" />
  </div>
);

/**
 * Row-level badges under one FieldsGroup:
 * - duplicate / built-in warning (error when it blocks submit, warning otherwise);
 * - completeness rule (rows with several importantFields): error when the row is half-filled,
 *   otherwise a focus hint.
 * The container is hidden until the row is focused when it only holds the focus hint.
 */
export const FieldsGroupBadges: FC<{
  duplicateMessage?: string;
  duplicateType: "error" | "warning";
  incomplete: boolean;
  /** Only set when the row has several importantFields. */
  completenessMessage?: string;
}> = ({ duplicateMessage, duplicateType, incomplete, completenessMessage }) => {
  const hasVisibleBadge = !!duplicateMessage || incomplete;
  if (!hasVisibleBadge && !completenessMessage) return null;

  return (
    <div
      className={`py-2 px-4 justify-center gap-2 ${hasVisibleBadge ? "grid" : "hidden group-focus-within/row:grid"}`}
    >
      {duplicateMessage && (
        <RowBadge message={duplicateMessage} type={duplicateType} />
      )}
      {completenessMessage &&
        (incomplete ? (
          <RowBadge message={completenessMessage} type="error" />
        ) : (
          <FocusHintBadge message={completenessMessage} />
        ))}
    </div>
  );
};

/**
 * Badge under an input with a `pattern`. Must sit inside a `group/field` wrapper together with that
 * input: the browser's own :invalid state decides, so it matches the native check that blocks submit.
 * - invalid: error (or warning when FIELDS_PROPS' blockSubmitOnInvalidInputs is false);
 * - valid (incl. empty) and focused: neutral hint; otherwise hidden.
 */
export const PatternBadge: FC<{
  message: string;
  type: "error" | "warning";
}> = ({ message, type }) => (
  <>
    <div className="hidden py-2 px-4 group-has-[input:invalid]/field:block">
      <RowBadge message={message} type={type} />
    </div>
    <div className="hidden py-2 px-4 group-focus-within/field:group-has-[input:valid]/field:block">
      <RowBadge message={message} type="neutral" />
    </div>
  </>
);
